"use client";

const AI_DB_NAME = "odin-ai-local";
const AI_DB_VERSION = 6;

export const AI_SESSIONS_STORE = "ai_chat_sessions";
export const AI_HISTORY_STORE = "ai_message_history";
export const AI_BROWSING_STORE = "ai_browsing_events";
export const AI_SESSION_MEMORY_STORE = "ai_session_memory";
export const AI_ENTITY_MEMORY_STORE = "ai_entity_memory";
export const AI_TOKEN_RESOLUTION_MEMORY_STORE = "ai_token_resolution_memory";
export const AI_CUSTOM_CONNECTIONS_STORE = "ai_custom_connections";
export const AI_EVIDENCE_STORE = "ai_evidence_records";
export const AI_CHARACTER_CARDS_STORE = "ai_character_cards";

export type AiStoreName =
	| typeof AI_SESSIONS_STORE
	| typeof AI_HISTORY_STORE
	| typeof AI_BROWSING_STORE
	| typeof AI_SESSION_MEMORY_STORE
	| typeof AI_ENTITY_MEMORY_STORE
	| typeof AI_TOKEN_RESOLUTION_MEMORY_STORE
	| typeof AI_CUSTOM_CONNECTIONS_STORE
	| typeof AI_EVIDENCE_STORE
	| typeof AI_CHARACTER_CARDS_STORE;

let openDbPromise: Promise<IDBDatabase> | null = null;
let activeDb: IDBDatabase | null = null;

function createStores(db: IDBDatabase) {
	if (!db.objectStoreNames.contains(AI_SESSIONS_STORE)) {
		const sessions = db.createObjectStore(AI_SESSIONS_STORE, {
			keyPath: "id",
		});
		sessions.createIndex("updatedAt", "updatedAt");
	}

	if (!db.objectStoreNames.contains(AI_HISTORY_STORE)) {
		const history = db.createObjectStore(AI_HISTORY_STORE, {
			keyPath: "storageKey",
		});
		history.createIndex("threadId", "threadId");
		history.createIndex("updatedAt", "updatedAt");
	}

	if (!db.objectStoreNames.contains(AI_BROWSING_STORE)) {
		const browsing = db.createObjectStore(AI_BROWSING_STORE, {
			keyPath: "id",
		});
		browsing.createIndex("timestamp", "timestamp");
		browsing.createIndex("pathname", "pathname");
	}

	if (!db.objectStoreNames.contains(AI_SESSION_MEMORY_STORE)) {
		const sessionMemory = db.createObjectStore(AI_SESSION_MEMORY_STORE, {
			keyPath: "threadId",
		});
		sessionMemory.createIndex("updatedAt", "updatedAt");
	}

	if (!db.objectStoreNames.contains(AI_ENTITY_MEMORY_STORE)) {
		const entityMemory = db.createObjectStore(AI_ENTITY_MEMORY_STORE, {
			keyPath: "key",
		});
		entityMemory.createIndex("updatedAt", "updatedAt");
		entityMemory.createIndex("entityType", "entityType");
		entityMemory.createIndex("entityId", "entityId");
	}

	if (!db.objectStoreNames.contains(AI_TOKEN_RESOLUTION_MEMORY_STORE)) {
		const resolutionMemory = db.createObjectStore(
			AI_TOKEN_RESOLUTION_MEMORY_STORE,
			{
				keyPath: "id",
			},
		);
		resolutionMemory.createIndex("normalizedQuery", "normalizedQuery");
		resolutionMemory.createIndex("tokenId", "tokenId");
		resolutionMemory.createIndex("updatedAt", "updatedAt");
		resolutionMemory.createIndex("lastSelectedAt", "lastSelectedAt");
	}

	if (!db.objectStoreNames.contains(AI_CUSTOM_CONNECTIONS_STORE)) {
		const connections = db.createObjectStore(AI_CUSTOM_CONNECTIONS_STORE, {
			keyPath: "id",
		});
		connections.createIndex("lastValidatedAt", "lastValidatedAt");
		connections.createIndex("provider", "provider");
	}

	if (!db.objectStoreNames.contains(AI_EVIDENCE_STORE)) {
		const evidence = db.createObjectStore(AI_EVIDENCE_STORE, {
			keyPath: "id",
		});
		evidence.createIndex("threadId", "threadId");
		evidence.createIndex("entityKey", "entityKey");
		evidence.createIndex("analysisType", "analysisType");
		evidence.createIndex("fetchedAt", "fetchedAt");
	}

	if (!db.objectStoreNames.contains(AI_CHARACTER_CARDS_STORE)) {
		const characterCards = db.createObjectStore(AI_CHARACTER_CARDS_STORE, {
			keyPath: "id",
		});
		characterCards.createIndex("source", "source");
		characterCards.createIndex("updatedAt", "updatedAt");
	}
}

export function isAiIndexedDbAvailable() {
	return typeof window !== "undefined" && "indexedDB" in window;
}

export async function openAiDb() {
	if (!isAiIndexedDbAvailable()) {
		throw new Error("IndexedDB is not available in the current environment.");
	}

	if (!openDbPromise) {
		openDbPromise = new Promise((resolve, reject) => {
			const request = window.indexedDB.open(AI_DB_NAME, AI_DB_VERSION);

			request.onupgradeneeded = () => {
				createStores(request.result);
			};

			request.onsuccess = () => {
				const db = request.result;
				activeDb = db;
				db.onversionchange = () => {
					db.close();
					if (activeDb === db) {
						activeDb = null;
					}
					openDbPromise = null;
				};
				resolve(db);
			};

			request.onblocked = () => {
				openDbPromise = null;
				reject(
					new Error(
						"AI local database upgrade is blocked by another open tab. Please close other Odin tabs and retry.",
					),
				);
			};

			request.onerror = () => {
				openDbPromise = null;
				reject(request.error ?? new Error("Failed to open AI IndexedDB."));
			};
		});
	}

	try {
		return await openDbPromise;
	} catch (error) {
		openDbPromise = null;
		throw error;
	}
}

export async function withStore<T>(
	storeName: AiStoreName,
	mode: IDBTransactionMode,
	handler: (store: IDBObjectStore, transaction: IDBTransaction) => Promise<T> | T,
) {
	const db = await openAiDb();
	const transaction = db.transaction(storeName, mode);
	const store = transaction.objectStore(storeName);

	const result = await handler(store, transaction);

	await new Promise<void>((resolve, reject) => {
		transaction.oncomplete = () => resolve();
		transaction.onerror = () =>
			reject(transaction.error ?? new Error(`Transaction failed for ${storeName}.`));
		transaction.onabort = () =>
			reject(transaction.error ?? new Error(`Transaction aborted for ${storeName}.`));
	});

	return result;
}

export function promisifyRequest<T>(request: IDBRequest<T>) {
	return new Promise<T>((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed."));
	});
}
